import type { NuxtApp } from 'nuxt/app'
import { $fetch } from 'ofetch'
import { DEFAULT_COUNTRY_CODE } from '@/utils/constants'

// locals

import AccountModule from '@/api/modules/account'
import SignUpModule from '@/api/modules/signUp'
import BoardModule from '@/api/modules/board'
import BannerModule from '@/api/modules/banner'
import TermsModule from '@/api/modules/terms'
import AuthModule from '@/api/modules/auth'
import ProfileModule from '@/api/modules/profile'
import ContactModule from '@/api/modules/contact'

export interface ApiModules {
  account: AccountModule
  signUp: SignUpModule
  board: BoardModule
  banner: BannerModule
  terms: TermsModule
  auth: AuthModule
  profile: ProfileModule
  contact: ContactModule
}

type CustomHeadersInit = HeadersInit & {
  Authorization?: string
  'x-mverse-language'?: string
  'x-mverse-countrycode'?: string
}

interface ResponseCountryCode {
  countryCode: CountryCodeOfGeoIP
}

// https://medium.com/@luizzappa/nuxt-3-repository-pattern-organising-and-managing-your-calls-to-apis-with-typescript-acd563a4e046
// https://www.vuemastery.com/blog/api-management-in-nuxt-3-with-typescript/
export default defineNuxtPlugin({
  name: 'api',
  async setup(nuxtApp: NuxtApp) {
    const config = useRuntimeConfig()
    const authStore = useAuthStore()
    const { countryCodeOfGeoIP } = storeToRefs(authStore)

    const isProdServer =
      import.meta.server && process.env.NODE_ENV === 'production'
    const headers = isProdServer ? useRequestHeaders() : undefined
    const baseURL = isProdServer
      ? config.apiClusterURL
      : config.public.apiBaseURL

    if (import.meta.server) {
      try {
        const resp = await $fetch<APIResponse<ResponseCountryCode>>(
          '/geo/v1/country',
          { headers, baseURL, timeout: 8000 }
        )
        console.log('geo resp', resp)
        authStore.setCountryCodeOfGeoIP(
          resp?.data?.countryCode || DEFAULT_COUNTRY_CODE
        )
        console.log('countryCodeOfGeoIP', countryCodeOfGeoIP.value)
      } catch {
        authStore.setCountryCodeOfGeoIP(DEFAULT_COUNTRY_CODE)
      }
    }

    const { accessToken } = useAuthToken()
    const fetchInstance = $fetch.create({
      headers,
      baseURL,
      timeout: 8000,
      onRequest({ request, options }) {
        // Log request
        console.log('[fetch request]', request /* options */)
        options.headers.set('x-mverse-language', nuxtApp.$i18n.locale.value)
        options.headers.set('x-mverse-countrycode', countryCodeOfGeoIP.value)

        if (options?.authorization && accessToken.value) {
          options.headers.set('Authorization', `Bearer ${accessToken.value}`)
        }

        console.log('headers?', options.headers)
      },

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onRequestError({ request, options, error }) {
        // Log error
        console.log('[fetch request error]', request /* options */)
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onResponse({ request, response, options }) {
        // Log response
        console.log(
          '[fetch response]',
          request,
          response.status /* response.body */
        )
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onResponseError({ request, response, options }) {
        // Log error
        console.log(
          '[fetch response error]',
          request,
          response.status,
          response.body
        )
      }
      // credentials: 'include'
    })
    const modules: ApiModules = {
      account: new AccountModule(fetchInstance),
      signUp: new SignUpModule(fetchInstance),
      board: new BoardModule(fetchInstance),
      banner: new BannerModule(fetchInstance),
      terms: new TermsModule(fetchInstance),
      auth: new AuthModule(fetchInstance),
      profile: new ProfileModule(fetchInstance),
      contact: new ContactModule(fetchInstance)
    }

    return {
      provide: {
        api: modules
      }
    }
  }
})
